import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ClipboardList, Building2, Layers3, Plus } from 'lucide-react';
import { setActiveTab } from '../../../../../uiSlice';

const VARIANT_ICON = {
  common: ClipboardList,
  independent: Building2,
  group: Layers3
};

const COPY = {
  common: {
    emptyTitle: 'No tasks available for this project',
    emptySubtitle: 'Tasks will appear here once they are created',
    filterTitle: 'No tasks match the selected filters',
    filterSubtitle: 'Try adjusting your filters to see more results'
  },
  independent: {
    emptyTitle: 'No tasks available for this project',
    emptySubtitle: 'Tasks will appear here once they are created',
    filterTitle: 'No tasks match the selected filters',
    filterSubtitle: 'Try adjusting your filters to see more results'
  },
  group: {
    emptyTitle: 'No group tasks available for this project',
    emptySubtitle: 'Tasks will appear here once they are created',
    filterTitle: 'No tasks match the selected filters',
    filterSubtitle: 'Try adjusting your filters to see more results'
  }
};

/**
 * @param {'panel' | 'tableCell'} layout — panel: fills parent (e.g. full empty view); tableCell: compact for inside <td>
 */
const TaskManagementEmptyState = ({
  variant,
  liveTasksTab,
  hasActiveFilter,
  onClearFilters,
  layout = 'panel'
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const Icon = VARIANT_ICON[variant] || ClipboardList;
  const text = COPY[variant] || COPY.common;

  const title = hasActiveFilter ? text.filterTitle : text.emptyTitle;
  const subtitle = hasActiveFilter ? text.filterSubtitle : text.emptySubtitle;
  const showCreate = !hasActiveFilter;

  const goCreate = () => {
    dispatch(setActiveTab(liveTasksTab));
    navigate('/builder/operations/tasks');
  };

  const rootClass =
    layout === 'tableCell'
      ? 'flex flex-col items-center justify-center gap-4 text-gray-400 px-4 py-10 min-h-[min(320px,calc(100vh-14rem))]'
      : 'flex flex-col items-center justify-center h-full min-h-[min(320px,calc(100vh-14rem))] gap-4 text-gray-400 px-4';

  return (
    <div className={rootClass}>
      <div className="relative mb-1">
        <div className="rounded-2xl bg-[#2A565E]/10 p-6 ring-1 ring-[#2A565E]/15 shadow-sm">
          <Icon className="w-12 h-12 text-[#2A565E]" strokeWidth={1.35} aria-hidden />
        </div>
        {showCreate && (
          <div
            className="absolute -right-1 -bottom-1 rounded-full bg-[#2A565E] p-2 shadow-md ring-4 ring-white"
            aria-hidden>
            <Plus className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
        )}
      </div>
      <span className="text-base font-medium text-gray-500 text-center max-w-md">{title}</span>
      <span className="text-sm text-gray-400 text-center max-w-sm">{subtitle}</span>
      <div className="flex flex-col sm:flex-row items-center gap-2 mt-1">
        {showCreate && (
          <button
            type="button"
            onClick={goCreate}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-[8px] text-sm font-medium bg-[#2A565E] text-white border border-[#2A565E] transition-all duration-200 hover:opacity-95 shadow-sm">
            <Plus className="w-4 h-4 shrink-0" strokeWidth={2.25} aria-hidden />
            Create tasks
          </button>
        )}
        {hasActiveFilter && onClearFilters && (
          <button
            type="button"
            onClick={onClearFilters}
            className="px-4 py-2 text-sm text-[#2A565E] bg-[#2A565E]/10 rounded-md hover:bg-[#2A565E]/20 transition-colors">
            Clear all filters
          </button>
        )}
      </div>
    </div>
  );
};

export default TaskManagementEmptyState;
